"use client";
import { useEffect, useRef, type RefObject } from "react";

/** Keep keyboard navigation and Escape inside an active modal; restore its trigger. */
export function useDialogFocus(open: boolean, panel: RefObject<HTMLElement | null>, onClose: () => void) {
  const close = useRef(onClose);
  useEffect(() => { close.current = onClose; });
  useEffect(() => {
    if (!open) return;
    const previousOverflow=document.body.style.overflow;
    document.body.style.overflow="hidden";
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const selector = 'button:not(:disabled),a[href],input:not(:disabled):not([type=hidden]),select:not(:disabled),textarea:not(:disabled),summary,[tabindex="0"]';
    const items = () => Array.from(panel.current?.querySelectorAll<HTMLElement>(selector) ?? []).filter(element => element.getClientRects().length > 0);
    const frame = requestAnimationFrame(() => {
      // Opening focus must not steal a field the user has already reached.
      if (panel.current?.contains(document.activeElement)) return;
      const controls = items();
      const initial = panel.current?.dataset.reading === 'true'
        ? panel.current
        : controls.find(element => element.matches('input:not([type=checkbox]),textarea')) ?? controls[0];
      initial?.focus({preventScroll:true});
    });
    function key(event: KeyboardEvent) {
      // Only the topmost dialog handles the event when confirmations overlay a ticket.
      const dialogs = Array.from(document.querySelectorAll('[role="dialog"]'));
      if (dialogs.at(-1) !== panel.current) return;
      if (event.key === "Escape") { event.preventDefault(); event.stopPropagation(); close.current(); }
      if (event.key === "Tab") {
        const controls = items();
        if (!controls.length) { event.preventDefault(); return; }
        const first = controls[0], last = controls[controls.length - 1];
        if (event.shiftKey && (document.activeElement === first || !panel.current?.contains(document.activeElement))) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && (document.activeElement === last || !panel.current?.contains(document.activeElement))) { event.preventDefault(); first.focus(); }
      }
    }
    document.addEventListener("keydown", key, true);
    return () => { document.body.style.overflow=previousOverflow;cancelAnimationFrame(frame); document.removeEventListener("keydown", key, true); previous?.focus(); };
  }, [open, panel]);
}
